// Settings → Notifications — who is e-mailed when an order lands in a tab (Fernando,
// 2026-09-29: "if a Nora Mikken order lands to Can't find … notify Maret"), or when a new item
// appears in a Review source (2026-10-26: a shipping method that reaches no carrier, a shop
// that renamed one — "just me"). Rules here, the mailbox under Connectors → Mail. Backend:
// backend/notifications.py (rules, outbox, the sweeper that composes and sends after the delay).
import { useCallback, useEffect, useState } from 'react';
import { API, authFetch } from './api';

const CARD = 'bg-[#1a1d27] rounded-xl border border-gray-800 px-5 py-4';
const INPUT = 'px-3 py-2 rounded bg-[#0f1117] border border-gray-700 text-white text-sm focus:outline-none focus:border-blue-500';

interface Rule {
  id: number; event: string; review_source: string | null;
  webshop_id: string | null; store_name: string | null; tab: string | null; tab_label: string;
  recipients: string[]; delay_minutes: number; enabled: boolean; last_sent_at: string | null; sent_count: number;
}
interface Data {
  rules: Rule[]; tabs: { key: string; label: string }[]; stores: { id: string; name: string }[];
  people: { username: string; email: string }[]; mail_configured: boolean; mail_from: string | null;
  review_sources: { key: string; label: string }[];
}
const TAB = 'order_entered_tab';
const REVIEW = 'review_item';
const INBOUND = 'inbound_done';                   // an inbound confirmed done → book it in WGR (2.11)
const EMPTY = { id: 0, event: TAB, webshop_id: '', tab: 'cant_find', review_source: '', recipients: '', delay_minutes: 3,
                enabled: true };

export default function NotificationsTab() {
  const [data, setData] = useState<Data | null>(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState<typeof EMPTY | null>(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [outbox, setOutbox] = useState<any[]>([]);

  const load = useCallback(() => {
    authFetch(`${API}/notifications/rules`)
      .then(async r => { if (!r.ok) throw new Error((await r.json()).detail || `HTTP ${r.status}`); return r.json(); })
      .then(d => { setData(d); setError(''); })
      .catch(e => setError(e.message));
    authFetch(`${API}/notifications/outbox?limit=30`).then(r => r.ok ? r.json() : { rows: [] })
      .then(d => setOutbox(d.rows || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form) return;
    const recipients = form.recipients.split(/[,;\s]+/).map(s => s.trim()).filter(Boolean);
    setBusy(true); setMsg('');
    const res = await authFetch(`${API}/notifications/rules`, {
      method: 'PUT', body: JSON.stringify({
        id: form.id || undefined, event: form.event, webshop_id: form.webshop_id || null, tab: form.tab,
        review_source: form.review_source, recipients, delay_minutes: form.delay_minutes, enabled: form.enabled }),
    });
    const d = await res.json().catch(() => ({}));
    setBusy(false);
    if (!res.ok) { setMsg(d.detail || `Could not save (HTTP ${res.status})`); return; }
    setMsg('✓ Rule saved — it applies from the next sweep (within a minute).');
    setForm(null); load();
  };
  const remove = async (r: Rule) => {
    if (!window.confirm(`Delete the rule ${r.store_name || 'Any store'} × ${r.tab_label} → ${r.recipients.join(', ')}?`)) return;
    const res = await authFetch(`${API}/notifications/rules/${r.id}`, { method: 'DELETE' });
    if (!res.ok) { setMsg(`Could not delete (HTTP ${res.status})`); return; }
    load();
  };
  const toggle = async (r: Rule) => {
    const res = await authFetch(`${API}/notifications/rules`, {
      method: 'PUT', body: JSON.stringify({ id: r.id, event: r.event, webshop_id: r.webshop_id, tab: r.tab,
                                            review_source: r.review_source, recipients: r.recipients,
                                            delay_minutes: r.delay_minutes, enabled: !r.enabled }) });
    if (!res.ok) { const d = await res.json().catch(() => ({})); setMsg(d.detail || `Could not save (HTTP ${res.status})`); return; }
    load();
  };
  const retry = async (id: number) => {
    const res = await authFetch(`${API}/notifications/outbox/${id}/retry`, { method: 'POST' });
    if (!res.ok) { const d = await res.json().catch(() => ({})); setMsg(d.detail || `Could not retry (HTTP ${res.status})`); return; }
    load();
  };
  const edit = (r: Rule) => setForm({ id: r.id, event: r.event || TAB, webshop_id: r.webshop_id || '', tab: r.tab || 'cant_find',
                                       review_source: r.review_source || '', recipients: r.recipients.join(', '),
                                       delay_minutes: r.delay_minutes, enabled: r.enabled });
  const fmt = (s: string | null) => s ? new Date(s).toLocaleString() : '—';
  const statusCls: Record<string, string> = { sent: 'text-green-400', pending: 'text-amber-300', sending: 'text-amber-300', failed: 'text-red-400',
                                              skipped: 'text-gray-500' };

  return (
    <div className="space-y-5 max-w-4xl">
      <p className="text-gray-500 text-sm">
        Internal e-mails from the warehouse mailbox — for the people who need to act, not for customers.
        A rule = store (or any) × <b>a tab an order lands in</b> (the order gets a comment saying who was told)
        or <b>a Review list</b> (one mail lists its new items; an item fixed before the mail is due is left out,
        one still open a day later is in the next mail) → recipients.
        {data && !data.mail_configured && (
          <span className="text-amber-400"> The mailbox is not set up yet — matched rules queue and wait until it is (Connectors → Mail).</span>)}
        {data?.mail_configured && <span className="text-gray-400"> Sending as {data.mail_from}.</span>}
      </p>

      <div className={CARD}>
        <div className="flex items-center justify-between mb-3">
          <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold">Rules</div>
          <button onClick={() => { setForm({ ...EMPTY }); setMsg(''); }}
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg">+ Add rule</button>
        </div>
        {error && <div className="text-xs text-red-400 mb-2">{error}</div>}
        {data && data.rules.length === 0 && <div className="text-gray-500 text-sm">No rules yet.</div>}
        {data && data.rules.length > 0 && (
          <table className="w-full text-sm">
            <thead><tr className="text-gray-500 text-xs uppercase tracking-wider text-left">
              <th className="py-1 pr-3">Store</th><th className="py-1 pr-3">When</th><th className="py-1 pr-3">Recipients</th>
              <th className="py-1 pr-3">Delay</th><th className="py-1 pr-3">Sent</th><th className="py-1 pr-3">Last</th><th className="py-1"></th></tr></thead>
            <tbody>
              {data.rules.map(r => (
                <tr key={r.id} className={`border-t border-gray-800 ${r.enabled ? '' : 'opacity-50'}`}>
                  <td className="py-2 pr-3 text-gray-200">{r.store_name || <span className="text-gray-500">Any store</span>}</td>
                  <td className="py-2 pr-3 text-gray-200">{r.tab_label}</td>
                  <td className="py-2 pr-3 text-gray-300">{r.recipients.join(', ')}</td>
                  <td className="py-2 pr-3 text-gray-400">{r.delay_minutes} min</td>
                  <td className="py-2 pr-3 text-gray-400">{r.sent_count}</td>
                  <td className="py-2 pr-3 text-gray-500 whitespace-nowrap">{fmt(r.last_sent_at)}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button onClick={() => toggle(r)} className="text-xs text-gray-400 hover:text-white mr-3">{r.enabled ? 'Pause' : 'Enable'}</button>
                    <button onClick={() => edit(r)} className="text-xs text-gray-400 hover:text-white mr-3">Edit</button>
                    <button onClick={() => remove(r)} className="text-xs text-gray-500 hover:text-red-300">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {form && data && (
          <div className="mt-4 border-t border-gray-800 pt-4 grid gap-3 md:grid-cols-2">
            <label className="text-xs text-gray-500">Store
              <select value={form.webshop_id} onChange={e => setForm({ ...form, webshop_id: e.target.value })} className={`${INPUT} w-full mt-1`}>
                <option value="">Any store</option>
                {data.stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select></label>
            <label className="text-xs text-gray-500">When
              <select value={form.event} onChange={e => setForm({ ...form, event: e.target.value,
                        review_source: form.review_source || data.review_sources[0]?.key || '' })} className={`${INPUT} w-full mt-1`}>
                <option value={TAB}>an order lands in a tab</option>
                <option value={REVIEW}>a new item appears in Review</option>
                <option value={INBOUND}>an inbound is done — book it in WGR</option>
              </select></label>
            {form.event === INBOUND ? (
              <div className="text-xs text-gray-500 self-end pb-2">One mail per inbound a manager confirmed done and nobody marked booked yet:
                the WGR booking steps filled in, and every line with its scanned quantity and stock location. For the booking Jean-Claude.</div>
            ) : form.event === TAB ? (
              <label className="text-xs text-gray-500">The order lands in
                <select value={form.tab} onChange={e => setForm({ ...form, tab: e.target.value })} className={`${INPUT} w-full mt-1`}>
                  {data.tabs.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select></label>
            ) : (
              <label className="text-xs text-gray-500">The Review list
                <select value={form.review_source} onChange={e => setForm({ ...form, review_source: e.target.value })} className={`${INPUT} w-full mt-1`}>
                  {data.review_sources.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select></label>
            )}
            <label className="text-xs text-gray-500 md:col-span-2">Recipients (comma-separated)
              <input value={form.recipients} onChange={e => setForm({ ...form, recipients: e.target.value })}
                list="notif-people" placeholder="name@motleydenim.com, …" className={`${INPUT} w-full mt-1`} />
              <datalist id="notif-people">{data.people.map(p => <option key={p.username} value={p.email}>{p.username}</option>)}</datalist>
            </label>
            <label className="text-xs text-gray-500">{form.event === TAB ? 'Send after (minutes) — lets the missing lines be marked first'
                                                         : 'Send after (minutes) — a problem fixed by then is left out'}
              <input type="number" min={0} max={1440} value={form.delay_minutes}
                onChange={e => setForm({ ...form, delay_minutes: Number(e.target.value) })} className={`${INPUT} w-full mt-1`} /></label>
            <label className="text-xs text-gray-500 flex items-end gap-2 pb-2">
              <input type="checkbox" checked={form.enabled} onChange={e => setForm({ ...form, enabled: e.target.checked })} /> Enabled</label>
            <div className="md:col-span-2 flex gap-2">
              <button onClick={save} disabled={busy} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-sm font-medium rounded-lg">{busy ? 'Saving…' : 'Save rule'}</button>
              <button onClick={() => setForm(null)} className="px-3 py-2 bg-gray-800 text-gray-400 text-sm rounded-lg">Cancel</button>
            </div>
          </div>
        )}
        {msg && <div className={`text-xs mt-2 ${msg.startsWith('✓') ? 'text-green-400' : 'text-red-400'}`}>{msg}</div>}
      </div>

      <div className={CARD}>
        <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-3">Last notifications</div>
        {outbox.length === 0 && <div className="text-gray-500 text-sm">Nothing queued or sent yet.</div>}
        {outbox.length > 0 && (
          <table className="w-full text-xs">
            <tbody>
              {outbox.map(o => (
                <tr key={o.id} className="border-t border-gray-800/60">
                  <td className="py-1.5 pr-3 text-gray-500 whitespace-nowrap">{fmt(o.created_at)}</td>
                  <td className="py-1.5 pr-3 text-gray-200 whitespace-nowrap">{o.order_number || o.order_id || (o.inbound_wgr ? `Inbound ${o.inbound_wgr}` : '') || (o.review_count ? `Review · ${o.review_count}` : '')}</td>
                  <td className="py-1.5 pr-3 text-gray-400">{(o.recipients || []).join(', ')}</td>
                  <td className={`py-1.5 pr-3 whitespace-nowrap ${statusCls[o.status] || 'text-gray-400'}`}>
                    {o.status}{o.status === 'pending' && o.next_attempt_at ? ` · ${fmt(o.next_attempt_at)}` : ''}{o.status === 'sent' && o.sent_at ? ` · ${fmt(o.sent_at)}` : ''}</td>
                  <td className="py-1.5 pr-3 text-gray-500 max-w-md truncate" title={o.error || o.subject || ''}>{o.error || o.subject || ''}</td>
                  <td className="py-1.5 text-right">{o.status === 'failed' && <button onClick={() => retry(o.id)} className="text-gray-400 hover:text-white">Retry</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
